import { fetchData, fetchPersonalData, getTokens } from '@/services';
import { selectUid } from '@/store';
import { authorizeDreamSDK } from '@/utils/dreamCRM';
import {
  BalanceType,
  BrandType,
  OperationType,
  OrderType,
  ProductType,
  UserType,
} from '@/types';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

type ProfileData = {
  user: UserType;
  orders: OrderType[];
  favorites: ProductType[];
  promos: OrderType[];
  brands: BrandType[];
};

export const useUser = ({ redirect = false }: { redirect?: boolean } = {}) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [favorites, setFavorites] = useState<ProductType[]>([]);
  const [promos, setPromos] = useState<OrderType[]>([]);
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [authVersion, setAuthVersion] = useState(0);
  const [bonuses, setBonuses] = useState<BalanceType>({
    available: 0,
    spent: 0,
    total: 0,
  });

  const [operations, setOperations] = useState<OperationType[]>([]);
  const { replace } = useRouter();
  const uid = useSelector(selectUid);

  const fetchAdditionalData = useCallback(async () => {
    const [favouritesRes, brandsRes, ordersRes, bonusesRes] = await Promise.all(
      [
        fetchData({ url: `/favorites/list?uid=${uid}` }),
        fetchData({ url: `/brand/favorites/list?uid=${uid}` }),
        fetchPersonalData({ url: '/orders?active=1' }),
        fetchPersonalData({ url: '/point/list' }),
      ],
    );

    if (favouritesRes.success) {
      setFavorites(favouritesRes.data.list);
    }

    if (brandsRes.success) {
      setBrands(brandsRes.data.list);
    }

    if (ordersRes.success) {
      setOrders(ordersRes.data.list);
    }

    if (bonusesRes.success) {
      setBonuses(bonusesRes.data.balance);
      setOperations(bonusesRes.data.list);
    }
  }, [uid]);

  useEffect(() => {
    const handleTokensEvent = () => {
      setAuthVersion((prev) => prev + 1);
    };

    window.addEventListener('auth:tokens', handleTokensEvent);
    return () => window.removeEventListener('auth:tokens', handleTokensEvent);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      setIsLoading(true);

      const profileResponse = await fetchPersonalData({
        url: '/profile',
      });

      if (profileResponse.success) {
        const { user, promos } = profileResponse.data as ProfileData;

        setUser(user);
        setPromos(promos);
        authorizeDreamSDK(user?.websiteId);
      } else {
        if (redirect) {
          replace('/');
        }
      }

      await fetchAdditionalData();

      setIsLoading(false);
    };

    const { accessToken } = getTokens();

    if (uid && accessToken) {
      fetchUser();
    }
  }, [redirect, replace, uid, fetchAdditionalData, authVersion]);

  return {
    user,
    orders,
    favorites,
    promos,
    brands,
    isLoading,
    bonuses,
    operations,
  };
};
